import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

// Redux
import { setOrderDataAction } from '../../../redux/Actions/CreateOrderActions';

// Components
import { Section, Placeholder, LoadingRow } from './ui';
import { SECTION_IDS, isPickupInstructionMissing, preferencesSummary } from './helpers';
import { preferencesData } from '../../../mockdata/common';

const DEFAULT_PICKUP = preferencesData.pickup_delivery.find((o) => o.default).value;

const LAUNDRY_FIELDS = [
  { key: 'products', label: 'Products' },
  { key: 'wash_temp', label: 'Wash temp' },
  { key: 'dryer_heat', label: 'Dryer heat' },
];

const NO_PREFERENCE = 'no preference';
const normalize = (v) =>
  String(v || '')
    .toLowerCase()
    .trim();

const TEXTAREA =
  'focus:border-client-300 focus:ring-client-300 mt-1.5 w-full rounded-md border border-slate-200 px-3 py-2 text-sm text-slate-800 placeholder:text-slate-400';

function PreferencePills({ label, options, value, onChange }) {
  const current = normalize(value) || NO_PREFERENCE;
  return (
    <div className='flex flex-col gap-1.5 sm:flex-row sm:items-start sm:gap-3'>
      <span className='w-28 shrink-0 text-xs font-medium text-slate-500 sm:pt-1.5'>{label}</span>
      <div role='radiogroup' aria-label={label} className='flex flex-wrap gap-1.5'>
        {options.map((o) => {
          const active = normalize(o.value) === current;
          return (
            <button
              key={o.id}
              type='button'
              role='radio'
              aria-checked={active}
              onClick={() => onChange(normalize(o.value) === NO_PREFERENCE ? null : o.value)}
              className={`rounded-md border px-3 py-1 text-xs font-semibold transition ${
                active
                  ? 'border-client-300 bg-client-300 text-white'
                  : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'
              }`}
            >
              {o.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default function PreferencesSection() {
  const dispatch = useDispatch();
  const { orderServiceAddress, orderPreferences } = useSelector((s) => s.createNewOrder);
  const { orderPreferences: addressPreferences, preferencesLoading } = useSelector((s) => s.myaccount);
  const preference = addressPreferences?.preference;

  // Address preferences become this order's preferences.
  useEffect(() => {
    const p = { ...preference, pickup_delivery: preference?.pickup_delivery || DEFAULT_PICKUP };
    if (normalize(p.pickup_delivery) !== 'other') delete p.pickup_delivery_instructions;
    dispatch(setOrderDataAction({ orderPreferences: p }));
  }, [preference, orderServiceAddress?.id]);

  const update = (changes) => dispatch(setOrderDataAction({ orderPreferences: { ...orderPreferences, ...changes } }));

  const selectPickup = (value) => {
    const { pickup_delivery_instructions, ...rest } = orderPreferences || {};
    dispatch(
      setOrderDataAction({
        orderPreferences: {
          ...rest,
          pickup_delivery: value,
          ...(value === 'Other' && { pickup_delivery_instructions: pickup_delivery_instructions || '' }),
        },
      })
    );
  };

  const disabled = !orderServiceAddress;
  const pickupChoice = normalize(orderPreferences?.pickup_delivery);
  const instructions = orderPreferences?.pickup_delivery_instructions || '';

  return (
    <Section
      id={SECTION_IDS.preferences}
      step={3}
      title='Preferences & instructions'
      subtitle='Helps your provider at pickup and delivery'
      summary={preferencesSummary(orderPreferences)}
      done={!disabled && !isPickupInstructionMissing(orderPreferences)}
      disabled={disabled}
      autoCollapse={false}
    >
      {disabled ? (
        <Placeholder>Choose a pickup address to set your preferences.</Placeholder>
      ) : preferencesLoading ? (
        <LoadingRow text='Loading preferences…' />
      ) : (
        <div className='space-y-4'>
          <div className='space-y-2.5'>
            <PreferencePills
              label='Pickup & delivery'
              options={preferencesData.pickup_delivery}
              value={orderPreferences?.pickup_delivery}
              onChange={selectPickup}
            />
            {pickupChoice === 'other' && (
              <div className='sm:pl-[8.5rem]'>
                <label htmlFor='order-pickup-instructions' className='text-xs font-semibold text-slate-600'>
                  Pickup and delivery instructions<span className='text-red-500'> *</span>
                </label>
                <textarea
                  id='order-pickup-instructions'
                  rows={2}
                  className={TEXTAREA}
                  placeholder='e.g. Bags will be by the side gate. Text me when you arrive.'
                  value={instructions}
                  onChange={(e) => update({ pickup_delivery_instructions: e.target.value })}
                />
                {instructions.trim().length < 5 && (
                  <p className='mt-1 text-xs text-slate-500'>Enter at least 5 characters.</p>
                )}
              </div>
            )}
            {LAUNDRY_FIELDS.map((f) => (
              <PreferencePills
                key={f.key}
                label={f.label}
                options={preferencesData[f.key]}
                value={orderPreferences?.[f.key]}
                onChange={(v) => update({ [f.key]: v })}
              />
            ))}
          </div>

          <div>
            <label htmlFor='order-other-details' className='text-xs font-semibold text-slate-600'>
              Other details<span className='font-normal text-slate-400'> · optional</span>
            </label>
            <textarea
              id='order-other-details'
              rows={2}
              className={TEXTAREA}
              placeholder='Anything else your provider should know'
              value={orderPreferences?.other_details || ''}
              onChange={(e) => update({ other_details: e.target.value || null })}
            />
          </div>
        </div>
      )}
    </Section>
  );
}
